import { animate, stagger } from "animejs";
import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  ChevronDown,
  Download,
  Filter,
  Loader2,
  Search,
  Sliders,
  X,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  animalTypes,
  bullTypes,
  coatColor,
  frameSizes,
  nigerianBreeds,
  ratios,
  sex,
} from "@/utils/items.ts";
import { apiClient } from "@/api/api.ts";
import AnimalGrid from "@/components/animal-grid.tsx";
import { FormValues } from "@/models/Sila.Animals.schema.ts";
import WhatsAppChat from "@/components/ui/whatsapp-chat.tsx";
import Banner from "@/features/ui/Banner";

// Type definitions
type SearchResult = {
  id: string;
  name: string;
  breed: string;
  sex: string;
  frameSize: string;
  coatColor: string;
  ratio: string;
  age: string;
  location: string;
  lastUpdated: string;
  imageUrl: string;
};

type FilterState = {
  animalType: string[];
  frameSizes: string[];
  sex: string[];
  coatColors: string[];
  ratios: string[];
  breeds: string[];
  bullTypes: string[];
};

// type SearchHistory = {
//   id: string;
//   query: string;
//   timestamp: Date;
//   resultCount: number;
//   filters: FilterState;
// };

export function AnimalBrowser() {
  // State
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searchProgress, setSearchProgress] = useState(0);
  const [searchStatus, setSearchStatus] = useState(false);
  const [searchPhase, setSearchPhase] = useState<
    "idle" | "indexing" | "analyzing" | "fetching" | "complete"
  >("idle");
  //@ts-ignore
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    animalType: [],
    frameSizes: [],
    sex: [],
    coatColors: [],
    ratios: [],
    breeds: [],
    bullTypes: [],
  });
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Add a new state to track if the filter dropdown is open
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: ["randomFourAnimals"],
      queryFn: async () => {
        const res = await apiClient.get("/animals/get-few-sila-Animals");
        return res.data;
      },
    });
  }, []);

  // Refs
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Mock suggestions based on query
  const mockSuggestions = {
    a: ["Angus", "Azawak", "Adamawa Gudali"],
    an: ["Angus"],
    b: ["Brahman", "Bunaji (White Fulani)", "Brown coat"],
    h: ["Holstein Freisian", "Heifers"],
    j: ["Jersey"],
  };

  // Effects
  useEffect(() => {
    if (searchContainerRef.current) {
      animate(searchContainerRef.current, {
        opacity: [0, 1],
        translateY: [20, 0],
        duration: 800,
        ease: "outExpo",
      });
    }
  }, []);

  useEffect(() => {
    // Toggle dark mode class on body
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  useEffect(() => {
    let progressInterval: NodeJS.Timeout | null = null;

    if (isSearching) {
      // Animate progress bar
      progressInterval = setInterval(() => {
        setSearchProgress((prev) => {
          const newProgress = prev + Math.random() * 5;

          // Update search phase based on progress
          if (
            newProgress > 25 &&
            newProgress <= 50 &&
            searchPhase === "indexing"
          ) {
            setSearchPhase("analyzing");
          } else if (
            newProgress > 50 &&
            newProgress <= 75 &&
            searchPhase === "analyzing"
          ) {
            setSearchPhase("fetching");
          } else if (newProgress > 75 && searchPhase === "fetching") {
            setSearchPhase("complete");
          }

          return newProgress > 95 ? 95 : newProgress;
        });
      }, 0);
    } else {
      setSearchProgress(0);
      setSearchPhase("idle");
    }

    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [isSearching]);

  useEffect(() => {
    // Generate suggestions based on query
    if (query.length > 0) {
      const firstChar = query.charAt(0).toLowerCase();
      const firstTwoChars = query.substring(0, 2).toLowerCase();

      let newSuggestions: string[] = [];

      if (mockSuggestions[firstTwoChars as keyof typeof mockSuggestions]) {
        newSuggestions =
          mockSuggestions[firstTwoChars as keyof typeof mockSuggestions];
      } else if (mockSuggestions[firstChar as keyof typeof mockSuggestions]) {
        newSuggestions =
          mockSuggestions[firstChar as keyof typeof mockSuggestions];
      }

      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [mockSuggestions, query]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + K to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }

      // Escape to clear search when focused
      if (
        e.key === "Escape" &&
        document.activeElement === searchInputRef.current
      ) {
        setQuery("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Inside the component
  const searchMutation = useMutation({
    mutationKey: ["allSilaAnimals"],
    mutationFn: async () => {
      // Construct query parameters correctly
      // with URLSearchParams
      const params = new URLSearchParams();

      if (query) params.append("query", query);
      activeFilters.frameSizes.forEach((size) =>
        params.append("frameSizes", size),
      );
      activeFilters.sex.forEach((s) => params.append("sex", s));
      activeFilters.coatColors.forEach((color) =>
        params.append("coatColors", color),
      );
      activeFilters.ratios.forEach((ratio) => params.append("ratios", ratio));
      activeFilters.breeds.forEach((breed) => params.append("breeds", breed));
      activeFilters.bullTypes.forEach((bullType) =>
        params.append("bullType", bullType),
      );

      console.log("params", params);
      const response = await apiClient.get(
        `/animals/get-all-Sila-Animals?${params.toString()}`,
      );

      return response.data;
    },
    onMutate: () => {
      // Start the search process and trigger
      // animations
      setIsSearching(true);
      setSearchPhase("indexing");
      setResults([]);
      setSearchProgress(5);
      setShowSuggestions(false);

      if (searchButtonRef.current) {
        animate(searchButtonRef.current, {
          scale: [1, 1.05, 1],
          duration: 1000,
          ease: "inOutQuad",
          loop: true,
        });
      }

      if (loadingRef.current) {
        animate(loadingRef.current.querySelectorAll(".loading-hexagon"), {
          scale: [0, 1],
          opacity: [0, 0.8, 0.2],
          delay: stagger(120),
          duration: 800,
          endDelay: 300,
          alternate: true,
          loop: true,
          ease: "outExpo",
        });
        animate(loadingRef.current, {
          rotate: "360deg",
          duration: 8000,
          ease: "linear",
          loop: true,
        });
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData<FormValues[]>(
        ["allSilaAnimals", activeFilters],
        data,
      );
      // Update results and search history
      setResults(data);

      // Animate the results
      if (resultsRef.current) {
        animate(resultsRef.current.querySelectorAll(".result-item"), {
          opacity: [0, 1],
          translateY: [15, 0],
          delay: stagger(100),
          duration: 800,
          ease: "outExpo",
        });
      }
    },
    onError: (error) => {
      console.error("Search failed:", error);
      // Optionally, set an error state to
      // display a message to the user
    },
    onSettled: () => {
      // Finalize the search process
      setIsSearching(false);
      setSearchProgress(100);
    },
  });
  const handleSearch = () => {
    searchMutation.mutate();
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    handleSearch();
    setSearchStatus(true);
  };
  const handleToggleFrameSize = (value: string) => {
    setActiveFilters((prev) => {
      const newFrameSizes = prev.frameSizes.includes(value)
        ? prev.frameSizes.filter((size) => size !== value)
        : [...prev.frameSizes, value];

      return {
        ...prev,
        frameSizes: newFrameSizes,
      };
    });
  };

  const handleToggleSex = (value: string) => {
    setActiveFilters((prev) => {
      const newSex = prev.sex.includes(value)
        ? prev.sex.filter((s) => s !== value)
        : [...prev.sex, value];

      return {
        ...prev,
        sex: newSex,
      };
    });
  };

  const handleToggleCoatColor = (value: string) => {
    setActiveFilters((prev) => {
      const newCoatColors = prev.coatColors.includes(value)
        ? prev.coatColors.filter((color) => color !== value)
        : [...prev.coatColors, value];

      return {
        ...prev,
        coatColors: newCoatColors,
      };
    });
  };

  const handleToggleBullType = (value: string) => {
    setActiveFilters((prev) => {
      const newBullTypes = prev.bullTypes?.includes(value)
        ? prev.bullTypes?.filter((type) => type !== value)
        : [...(prev.bullTypes || []), value];

      return {
        ...prev,
        bullTypes: newBullTypes,
      };
    });
  };
  const handleToggleAnimalType = (value: string) => {
    setActiveFilters((prev) => {
      const newAnimalTypes = prev.animalType?.includes(value)
        ? prev.bullTypes?.filter((type) => type !== value)
        : [...(prev.animalType || []), value];

      return {
        ...prev,
        bullTypes: newAnimalTypes,
      };
    });
  };

  const handleToggleRatio = (value: string) => {
    setActiveFilters((prev) => {
      const newRatios = prev.ratios.includes(value)
        ? prev.ratios.filter((ratio) => ratio !== value)
        : [...prev.ratios, value];

      return {
        ...prev,
        ratios: newRatios,
      };
    });
  };

  const handleToggleBreed = (value: string) => {
    setActiveFilters((prev) => {
      const newBreeds = prev.breeds.includes(value)
        ? prev.breeds.filter((breed) => breed !== value)
        : [...prev.breeds, value];

      return {
        ...prev,
        breeds: newBreeds,
      };
    });
  };

  const handleClearFilters = () => {
    setActiveFilters({
      animalType: [],
      frameSizes: [],
      sex: [],
      coatColors: [],
      ratios: [],
      breeds: [],
      bullTypes: [],
    });
  };

  const handleRemoveFilter = (type: keyof FilterState, value: string) => {
    setActiveFilters((prev) => {
      const newFilters = { ...prev };

      newFilters[type] = newFilters[type].filter((item) => item !== value);

      return newFilters;
    });
  };

  // Generate hexagon points for SVG
  const generateHexagonPoints = (size: number) => {
    const points = [];

    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI) / 3;
      const x = size * Math.cos(angle);
      const y = size * Math.sin(angle);

      points.push(`${x},${y}`);
    }

    return points.join(" ");
  };

  // Count total active filters
  const totalActiveFilters =
    activeFilters.animalType.length +
    activeFilters.frameSizes.length +
    activeFilters.sex.length +
    activeFilters.coatColors.length +
    activeFilters.ratios.length +
    activeFilters.breeds.length +
    activeFilters.bullTypes.length;

  return (
    <div
      className={`font-[suse] min-h-screen w-screen bg-gradient-to-b ${isDarkMode ? "from-gray-900 to-gray-950 text-gray-100" : "from-gray-50 to-gray-100 text-gray-800"} flex flex-col items-center justify-start  px-4 transition-colors duration-200`}
    >
      <Banner />

      <div ref={searchContainerRef} className="w-full max-w-4xl opacity-0">
        {/* <div className="flex justify-between items-center mb-6"> */}
        {/*   <div */}
        {/*     className={ */}
        {/*       "w-full lg:flex lg:flex-row justify-between items-center gap-4" + */}
        {/*       " sm:flex" + */}
        {/*       " sm:flex-col sm:justify-center sm:items-center" */}
        {/*     } */}
        {/*   > */}
        {/*     <img alt="Logo" className="h-16 z-20 " src={logo} /> */}

        {/*     <h1 className="text-3xl font-semibold mb-2"> */}
        {/*       /!* Livestock Search *!/ */}
        {/*       Choose Superior Genetics */}
        {/*     </h1> */}
        {/*   </div> */}
        {/* </div> */}

        {/* <p className={`mb-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}> */}
        {/*   /!* Find and filter livestock by breed, size, color, and other *!/ */}
        {/*   /!* characteristics *!/ */}
        {/*   Pasture to Profit – Buy Superior Beef & Dairy Semen & Embryos Built */}
        {/*   for the Tropics! Available Now at SilagreenAgro. */}
        {/* </p> */}

        <div className="relative mb-8">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <div className="relative">
                <Input
                  ref={searchInputRef}
                  className={`w-full h-12 pl-4 pr-24 rounded-lg ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                      : "border-gray-300 text-gray-900"
                  } shadow-sm focus:ring-2 focus:ring-gray-400 focus:border-transparent`}
                  disabled={isSearching}
                  placeholder="Search by name, breed, or other attributes... (Ctrl+K)"
                  type="text"
                  value={query}
                  onBlur={() =>
                    setTimeout(() => setShowSuggestions(false), 200)
                  }
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => query && setShowSuggestions(true)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
                {query && (
                  <Button
                    className="h-8 w-8 absolute right-3 top-1/2 transform -translate-y-1/2"
                    size="icon"
                    variant="ghost"
                    onClick={() => setQuery("")}
                  >
                    <X size={16} />
                  </Button>
                )}
              </div>

              {/* Auto-suggestions dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  className={`absolute z-10 w-full mt-1 rounded-md shadow-lg ${
                    isDarkMode
                      ? "bg-gray-800 border border-gray-700"
                      : "bg-white border border-gray-200"
                  }`}
                >
                  <ul className="py-1">
                    {suggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        className={`px-4 py-2 text-sm cursor-pointer flex items-center ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        }`}
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        <Search className="mr-2 opacity-50" size={14} />
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                ref={searchButtonRef}
                className={`h-12 px-6 ${
                  isDarkMode
                    ? "bg-[#015354ff] hover:opacity-95 hover:bg-[#015354ff]"
                    : "bg-[#015354ff] hover:opacity-95 hover:bg-[#015354ff]"
                } relative overflow-hidden`}
                disabled={isSearching}
                onClick={handleSearch}
              >
                {isSearching ? (
                  <span className="flex items-center">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Searching
                  </span>
                ) : (
                  <span className="flex items-center">
                    Search
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                )}
              </Button>

              <DropdownMenu open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="h-12 px-3 relative"
                    variant={isDarkMode ? "secondary" : "outline"}
                  >
                    <Filter size={18} />
                    {totalActiveFilters > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {totalActiveFilters}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className={`w-56 ${isDarkMode ? "bg-gray-800 border-gray-700" : ""}`}
                >
                  <DropdownMenuLabel>Livestock Filters</DropdownMenuLabel>
                  <DropdownMenuSeparator />

                  <DropdownMenuGroup>
                    {/* bull type */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <span>Bull Type</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        className={`max-h-[200px] overflow-y-auto ${isDarkMode ? "bg-gray-800 border-gray-700" : ""}`}
                      >
                        {bullTypes.map((types) => (
                          <DropdownMenuCheckboxItem
                            key={types.value}
                            checked={activeFilters.bullTypes.includes(
                              types.value,
                            )}
                            onCheckedChange={() =>
                              handleToggleBullType(types.value)
                            }
                            onSelect={(e) => e.preventDefault()}
                          >
                            <span>{types.label}</span>
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <span>Type</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        className={`max-h-[200px] overflow-y-auto ${isDarkMode ? "bg-gray-800 border-gray-700" : ""}`}
                      >
                        {animalTypes.map((types) => (
                          <DropdownMenuCheckboxItem
                            key={types.value}
                            checked={activeFilters.bullTypes.includes(
                              types.value,
                            )}
                            onCheckedChange={() =>
                              handleToggleAnimalType(types.value)
                            }
                            onSelect={(e) => e.preventDefault()}
                          >
                            <span>{types.label}</span>
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <span>Frame Size</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        className={
                          isDarkMode ? "bg-gray-800 border-gray-700" : ""
                        }
                      >
                        {frameSizes.map((size) => (
                          <DropdownMenuCheckboxItem
                            key={size.value}
                            checked={activeFilters.frameSizes.includes(
                              size.value,
                            )}
                            onCheckedChange={() =>
                              handleToggleFrameSize(size.value)
                            }
                            onSelect={(e) => e.preventDefault()} // Prevent
                            // closing
                            // on
                            // select
                          >
                            <span>{size.label}</span>
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <span>Sex</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        className={
                          isDarkMode ? "bg-gray-800 border-gray-700" : ""
                        }
                      >
                        {sex.map((item) => (
                          <DropdownMenuCheckboxItem
                            key={item.sex}
                            checked={activeFilters.sex.includes(item.sex)}
                            onCheckedChange={() => handleToggleSex(item.sex)}
                            onSelect={(e) => e.preventDefault()} // Prevent
                            // closing
                            // on
                            // select
                          >
                            <span>{item.label}</span>
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <span>Coat Color</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        className={`h-full overflow-y-auto ${
                          isDarkMode ? "bg-gray-800" + " border-gray-700" : ""
                        }`}
                      >
                        {coatColor.map((color) => (
                          <DropdownMenuCheckboxItem
                            key={color.coatColor}
                            checked={activeFilters.coatColors.includes(
                              color.coatColor,
                            )}
                            onCheckedChange={() =>
                              handleToggleCoatColor(color.coatColor)
                            }
                            onSelect={(e) => e.preventDefault()} // Prevent
                            // closing
                            // on
                            // select
                          >
                            <span>{color.label}</span>
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <span>Ratio</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        className={`max-h-[200px] overflow-y-auto ${isDarkMode ? "bg-gray-800 border-gray-700" : ""}`}
                      >
                        {ratios.map((ratio) => (
                          <DropdownMenuCheckboxItem
                            key={ratio.value}
                            checked={activeFilters.ratios.includes(ratio.value)}
                            onCheckedChange={() =>
                              handleToggleRatio(ratio.value)
                            }
                            onSelect={(e) => e.preventDefault()} // Prevent
                            // closing
                            // on
                            // select
                          >
                            <span>{ratio.label}</span>
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <span>Breed</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        className={`max-h-[200px] overflow-y-auto ${isDarkMode ? "bg-gray-800 border-gray-700" : ""}`}
                      >
                        {nigerianBreeds.map((breed) => (
                          <DropdownMenuCheckboxItem
                            key={breed.breed}
                            checked={activeFilters.breeds.includes(breed.breed)}
                            onCheckedChange={() =>
                              handleToggleBreed(breed.breed)
                            }
                            onSelect={(e) => e.preventDefault()} // Prevent
                            // closing
                            // on
                            // select
                          >
                            <span>{breed.label}</span>
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  </DropdownMenuGroup>

                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleClearFilters}>
                    Clear All Filters
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="p-2">
                    <Button
                      className="w-full"
                      size="sm"
                      onClick={() => setIsFilterOpen(false)}
                    >
                      Close
                    </Button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Active filters display */}
          {totalActiveFilters > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {activeFilters.frameSizes.map((size) => (
                <Badge
                  key={`frame-${size}`}
                  className="cursor-pointer"
                  variant={isDarkMode ? "secondary" : "outline"}
                  onClick={() => handleRemoveFilter("frameSizes", size)}
                >
                  {size} <X className="ml-1 inline" size={12} />
                </Badge>
              ))}

              {activeFilters.sex.map((s) => (
                <Badge
                  key={`sex-${s}`}
                  className="cursor-pointer"
                  variant={isDarkMode ? "secondary" : "outline"}
                  onClick={() => handleRemoveFilter("sex", s)}
                >
                  {s} <X className="ml-1 inline" size={12} />
                </Badge>
              ))}

              {activeFilters.coatColors.map((color) => (
                <Badge
                  key={`color-${color}`}
                  className="cursor-pointer"
                  variant={isDarkMode ? "secondary" : "outline"}
                  onClick={() => handleRemoveFilter("coatColors", color)}
                >
                  {color} <X className="ml-1 inline" size={12} />
                </Badge>
              ))}

              {activeFilters.ratios.map((ratio) => (
                <Badge
                  key={`ratio-${ratio}`}
                  className="cursor-pointer"
                  variant={isDarkMode ? "secondary" : "outline"}
                  onClick={() => handleRemoveFilter("ratios", ratio)}
                >
                  {ratio} <X className="ml-1 inline" size={12} />
                </Badge>
              ))}

              {activeFilters.breeds.map((breed) => (
                <Badge
                  key={`breed-${breed}`}
                  className="cursor-pointer"
                  variant={isDarkMode ? "secondary" : "outline"}
                  onClick={() => handleRemoveFilter("breeds", breed)}
                >
                  {breed} <X className="ml-1 inline" size={12} />
                </Badge>
              ))}

              {activeFilters.bullTypes.map((type) => (
                <Badge
                  key={`bullType-${type}`}
                  className="cursor-pointer"
                  variant={isDarkMode ? "secondary" : "outline"}
                  onClick={() => handleRemoveFilter("bullTypes", type)}
                >
                  {type} <X className="ml-1 inline" size={12} />
                </Badge>
              ))}

              {totalActiveFilters > 1 && (
                <Button
                  className="h-6 px-2 text-xs"
                  size="sm"
                  variant="ghost"
                  onClick={handleClearFilters}
                >
                  Clear All
                </Button>
              )}
            </div>
          )}

          <div className="flex mt-2 text-xs">
            <div className="ml-auto">
              <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                Press{" "}
                <kbd
                  className={`px-1.5 py-0.5 rounded ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                >
                  Ctrl+K
                </kbd>{" "}
                to search
              </span>
            </div>
          </div>
        </div>

        {/* {!isSearching && results.length === 0 && ( */}
        {/*   <AnimalGrid Animals={data ?? []} /> */}
        {/* )} */}

        {isSearching && searchStatus && (
          <div
            className={`space-y-6 my-10 ${isDarkMode ? "text-gray-300" : ""}`}
          >
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium">
                {searchPhase === "indexing" && "Indexing livestock database..."}
                {searchPhase === "analyzing" && "Analyzing breed patterns..."}
                {searchPhase === "fetching" && "Fetching matching livestock..."}
                {searchPhase === "complete" && "Finalizing results..."}
              </div>
              <div className="text-sm text-gray-500">
                {Math.round(searchProgress)}%
              </div>
            </div>

            <Progress
              className={`h-2 ${isDarkMode ? "[&>div]:bg-blue-500" : ""}`}
              value={searchProgress}
            />

            <div className="flex justify-center items-center h-40">
              <div ref={loadingRef} className="relative w-32 h-32">
                {/* Create hexagon loading pattern */}
                {[...Array(6)].map((_, i) => {
                  const angle = (i * Math.PI) / 3;
                  const x = 50 + 35 * Math.cos(angle);
                  const y = 50 + 35 * Math.sin(angle);

                  return (
                    <svg
                      key={i}
                      className="loading-hexagon absolute w-10 h-10 opacity-0"
                      style={{
                        left: `${x}%`,
                        top: `${y}%`,
                        transform: "translate(-50%, -50%)",
                      }}
                      viewBox="-25 -25 50 50"
                    >
                      <polygon
                        fill="none"
                        points={generateHexagonPoints(20)}
                        stroke={`hsl(${210 + i * 30}, 70%, 50%)`}
                        strokeWidth="2"
                      />
                    </svg>
                  );
                })}

                {/* Center circle with pulsing effect */}
                <div
                  className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-800"
                  } flex items-center justify-center text-white`}
                >
                  <Search size={20} />
                </div>
              </div>
            </div>

            <div className="flex justify-center space-x-8">
              {["Database", "Breeds", "Locations", "Health"].map(
                (source, i) => (
                  <div
                    key={i}
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} flex items-center`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full mr-1 ${
                        searchProgress > i * 25
                          ? isDarkMode
                            ? "bg-green-400"
                            : "bg-green-500"
                          : isDarkMode
                            ? "bg-gray-600"
                            : "bg-gray-300"
                      }`}
                    />
                    {source}
                  </div>
                ),
              )}
            </div>
          </div>
        )}

        {!isSearching && (
          <Tabs className="w-full" defaultValue="results">
            <TabsList className={`mb-6 ${isDarkMode ? "bg-gray-800" : ""}`}>
              <TabsTrigger value="results">
                Results {results.length > 0 && `(${results.length})`}
              </TabsTrigger>
              {/* <TabsTrigger value="history">Search History</TabsTrigger> */}
              {/* <TabsTrigger value="saved">Saved Searches</TabsTrigger> */}
              {/* <TabsTrigger value="analytics">Analytics</TabsTrigger> */}
            </TabsList>

            <TabsContent value="results">
              {results.length > 0 ? (
                <div ref={resultsRef} className="space-y-2 w-full">
                  <div className="flex flex-col md:flex-row lg:flex-row justify-between items-center mb-4">
                    <div className="flex items-center">
                      <h2
                        className={`text-xl font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
                      >
                        Livestock Results
                      </h2>
                      <div
                        className={`ml-3 px-3 py-1 rounded-full text-sm ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"}`}
                      >
                        {results.length}{" "}
                        {results.length === 1 ? "animal" : "animals"} found
                      </div>
                    </div>

                    <div className="w-full flex justify-between gap-2">
                      {/* <TooltipProvider> */}
                      {/*   <Tooltip> */}
                      {/*     <TooltipTrigger asChild> */}
                      {/*       <Button */}
                      {/*         className={ */}
                      {/*           isDarkMode */}
                      {/*             ? "border-gray-700 hover:bg-gray-700" */}
                      {/*             : "" */}
                      {/*         } */}
                      {/*         size="sm" */}
                      {/*         variant="outline" */}
                      {/*         // onClick={handleSaveSearch} */}
                      {/*       > */}
                      {/*         <BookmarkPlus className="mr-1" size={16} /> */}
                      {/*         Save */}
                      {/*       </Button> */}
                      {/*     </TooltipTrigger> */}
                      {/*     <TooltipContent> */}
                      {/*       <p>Save this search</p> */}
                      {/*     </TooltipContent> */}
                      {/*   </Tooltip> */}
                      {/* </TooltipProvider> */}

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              className={
                                isDarkMode
                                  ? "border-gray-700 hover:bg-gray-700"
                                  : ""
                              }
                              size="sm"
                              variant="outline"
                              // onClick={handleExportResults}>
                              onClick={() => setSearchStatus(false)}
                            >
                              <Download className="mr-1" size={16} />
                              Clear Searches
                            </Button>
                          </TooltipTrigger>
                          {/* <TooltipContent>
																 <p>Export results</p>
																 </TooltipContent> */}
                        </Tooltip>
                      </TooltipProvider>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            className={
                              isDarkMode
                                ? "border-gray-700 hover:bg-gray-700"
                                : ""
                            }
                            size="sm"
                            variant="outline"
                          >
                            <Sliders className="mr-1" size={16} />
                            Sort
                            <ChevronDown className="ml-1" size={14} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className={
                            isDarkMode ? "bg-gray-800 border-gray-700" : ""
                          }
                        >
                          <DropdownMenuItem>Name (A-Z)</DropdownMenuItem>
                          <DropdownMenuItem>Age (Youngest)</DropdownMenuItem>
                          <DropdownMenuItem>Age (Oldest)</DropdownMenuItem>
                          <DropdownMenuItem>Breed</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <AnimalGrid Animals={searchMutation.data} />
                </div>
              ) : (
                <div
                  className={`text-center py-12 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {query || totalActiveFilters > 0
                    ? "No livestock found matching your criteria."
                    : "Use the search or filters to find livestock."}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
      <WhatsAppChat
        phoneNumber="+234 912 800 6892"
        position="bottom-right"
        showTooltip={true}
      />
    </div>
  );
}
